import { t } from '@lingui/core/macro';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { StudentWorkPlacementContractParty } from '@duke-hq/svc-api-db/client';

type Contract = NonNullable<
  RouterOutputs['workPlacements']['placements']['journeyByEnrolment']['contract']
>;

export function partyLabel(party: StudentWorkPlacementContractParty): string {
  switch (party) {
    case StudentWorkPlacementContractParty.EMPLOYER:
      return t`Employer`;
    case StudentWorkPlacementContractParty.STUDENT:
      return t`Student`;
    case StudentWorkPlacementContractParty.DUKE:
      return t`Duke`;
  }
}

export function placingBlockedReason({
  enrolmentWithdrawn,
  journeyOnHold,
  contract,
  unitsToPlace,
  unitsLoadFailed,
}: {
  enrolmentWithdrawn: boolean;
  journeyOnHold: boolean;
  contract: Pick<Contract, 'isComplete' | 'employerDoNotUse' | 'signatures'> | null;
  unitsToPlace: number | undefined;
  unitsLoadFailed: boolean;
}): string | null {
  if (enrolmentWithdrawn) {
    return t`The enrolment has been withdrawn.`;
  }
  if (journeyOnHold) {
    return t`This student's placement journey is on hold.`;
  }
  return (
    contractBlocksPlacingReason(contract) ??
    employerBlocksPlacingReason(contract) ??
    unitsBlockPlacingReason(unitsToPlace, unitsLoadFailed)
  );
}

function employerBlocksPlacingReason(
  contract: Pick<Contract, 'employerDoNotUse'> | null,
): string | null {
  return contract?.employerDoNotUse === true
    ? t`This employer is marked as do not use, so the student cannot be placed with them.`
    : null;
}

function unitsBlockPlacingReason(
  unitsToPlace: number | undefined,
  unitsLoadFailed: boolean,
): string | null {
  if (unitsLoadFailed) {
    return t`Unable to load the units for this enrolment.`;
  }
  return unitsToPlace === 0 ? t`No outstanding work placement units for this enrolment.` : null;
}

function contractBlocksPlacingReason(
  contract: Pick<Contract, 'isComplete' | 'signatures'> | null,
): string | null {
  if (contract == null) {
    return t`Create the contract first.`;
  }
  if (contract.isComplete) {
    return null;
  }
  const outstanding = new Intl.ListFormat('en-AU', { type: 'conjunction' }).format(
    contract.signatures
      .filter((signature) => signature.receivedAt == null)
      .map((signature) => partyLabel(signature.party)),
  );
  return t`Waiting on signatures from ${outstanding}.`;
}
